import React, { useEffect, useState, useRef } from "react";
import classNames from 'classnames';
import { useDispatch } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice, isPriceVariationsEnabled } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';
import { getOwnJobRfpListings, getUserDetails } from '../../util/api';
import {
  saveConsentFor,
  ConsentSaveFailedError,
  isConsentRequiredRefusal,
  CONSENT_SAVE_FAILED_MESSAGE_ID,
} from '../../util/actionConsent';
import { hasVerifiedCategory, isCompanyVerified } from '../../util/verifiedStatus';
import AddToPreferredButton, { canAddToPreferred } from '../AddToPreferredButton/AddToPreferredButton';
import { isInvitedToPosting } from '../../util/postingInvitation';
import { sendInvitationToUser } from '../../containers/JobPostsPage/JobPostsPage.duck';

import { AspectRatioWrapper, NamedLink, ResponsiveImage, ConsultantSelectionModal } from '../../components';
import ApplicationResumeModal from '../ApplicationResumeModal/ApplicationResumeModal';
import UploadDocumentButton from '../UploadDocumentButton/UploadDocumentButton';
import {
  Card,
  Avatar,
  Tag,
  Button,
  Typography,
  Space,
  Divider,
  Tooltip,
  Row,
  Col,
  Checkbox,
  message,
  Modal,
  Select,
} from "antd";
import {
  CheckCircleOutlined,
  VideoCameraOutlined,
  BankOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  TeamOutlined,
  SafetyCertificateOutlined,
  OneToOneOutlined,
  TrophyOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  SolutionOutlined,
  FileSearchOutlined,
  DollarOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  LaptopOutlined,
  FileProtectOutlined,
  SafetyOutlined,
  UserOutlined,
  DownOutlined,
  UpOutlined,
  BarChartOutlined,
  FileDoneOutlined,
  BookOutlined,
  AccountBookFilled,
  PicCenterOutlined,
  CheckOutlined,
  CloseCircleOutlined,
  UnorderedListOutlined,
  ExportOutlined,
  FilePdfOutlined,
  FileOutlined,
} from "@ant-design/icons";
import css from './ListingCard.module.css';
import Spinner from '../IconSpinner/IconSpinner';
import { createResourceLocatorString } from '../../util/routes';
import { useHistory, useLocation } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { userTypeOf } from '../../util/userType';
import { agencyApplyWithConsultant, NO_ACCEPTED_CONSULTANT } from '../../util/linkedConsultantLists';
import { offeredJobRequirements } from '../../util/unbuiltVerifications';

const { Text, Paragraph, Title } = Typography;

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isPriceVariationsInUse = isPriceVariationsEnabled(publicData, foundListingTypeConfig);
  const hasMultiplePriceVariants = isPriceVariationsInUse && publicData?.priceVariants?.length > 1;

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);

  const priceValue = <span className={css.priceValue}>{formattedPrice}</span>;
  const pricePerUnit = isBookable ? (
    <span className={css.perUnit}>
      <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
    </span>
  ) : (
    ''
  );

  return (
    <div className={css.price} title={priceTitle}>
      {hasMultiplePriceVariants ? (
        <FormattedMessage
          id="ListingCard.priceStartingFrom"
          values={{ priceValue, pricePerUnit }}
        />
      ) : (
        <FormattedMessage id="ListingCard.price" values={{ priceValue, pricePerUnit }} />
      )}
    </div>
  );
};

const formatPrice = (price) => {
  if (!price || !price.unit_amount) return '';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price?.currency?.toUpperCase(),
  }).format(price?.unit_amount / 100);
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {boolean?} props.showAuthorInfo
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */

const formatDate = dateString => {
  if (!dateString) return '-';
  // Handle both ISO timestamps and plain dates from listing metadata.
  const date = new Date(dateString);
  const fallbackDate = isNaN(date.getTime()) ? new Date(dateString.replace(/-/g, '/')) : date;
  if (isNaN(fallbackDate.getTime())) {
    return dateString; // Return original string if invalid
  }
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  }).format(fallbackDate);
};

export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    showAuthorInfo = true,
    currentUser,
    currentUserAdminId,
    onInitiateApplyTransaction,
    acceptedConsultantsExist,
    onSearchAcceptedConsultants,
    profileListing,
    listingApplyTransaction,
    numberOfActiveApplications = 0,
    subscriptionStatus,
    onPayForRFP,
    subscriptionPrices,
    currentUserPersonaRecords,
    onApplyAttemptWithoutAuth,
    onUploadBlobFile,
    onUploadResumeBlobFile,
    // Shown at the bottom of the card: the Preferred Network pages put an entry's status, who added
    // it, its tags, note and actions here.
    footer = null,
  } = props;

  // Normalize listing object and guard early to avoid rendering
  // partial/fallback UI while async listing data is loading.
  const currentListing = ensureListing(listing);
  if (!currentListing || !currentListing.id || !currentListing.attributes) {
    return null;
  }

  const footerArea = footer ? (
    <div className={css.networkFooter} data-testid="listing-card-footer">
      {footer}
    </div>
  ) : null;

  const listingType = currentListing?.attributes?.publicData?.listingType;
  const isPost = listingType === 'job_listing' || listingType === 'rfp_listing';

  const classes = classNames(rootClassName || (isPost ? css.rootPost : css.root), className);

  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  // What a job asks of applicants, without what can no longer be asked for.
  const jobRequirements = offeredJobRequirements(publicData?.verificationRequirementsJob);
  const slug = createSlug(title);
  const author = ensureUser(listing.author);

  if (currentListing?.attributes?.publicData?.isDraftListing) {
    return null;
  }

  const listingIsJob = listingType === 'job_listing';
  const listingIsRFP = listingType === 'rfp_listing';
  const listingIsCandidate = listingType === 'candidate';
  const listingIsAgency = listingType === 'agency';
  const listingIsCustomer = listingType === 'customer';
  const listingPublicId =
    publicData?.jobRfpId
    || (listingIsRFP
        ? publicData?.rfpNumberIdRFP
        : null);

  const [isIdentityVerified, setIsIdentityVerified] = useState(false);
  const [isWorkVerified, setIsWorkVerified] = useState(false);
  const [isEducationVerified, setIsEducationVerified] = useState(false);
  const [isExperienceVerified, setIsExperienceVerified] = useState(false);

  const [isListingCompanyVerified, setIsListingCompanyVerified] = useState(false);

  const [isCurrentUserIdentityVerified, setIsCurrentUserIdentityVerified] = useState(false);
  const [isCurrentUserWorkVerified, setIsCurrentUserWorkVerified] = useState(false);
  const [isCurrentUserEducationVerified, setIsCurrentUserEducationVerified] = useState(false);
  const [isCurrentUserExperienceVerified, setIsCurrentUserExperienceVerified] = useState(false);

  const [isCurrentUserCompanyVerified, setIsCurrentUserCompanyVerified] = useState(false);

  const currentUserMainListingId = currentUser?.attributes?.profile?.publicData?.profileListingId || currentUser?.attributes?.profile?.publicData?.firstListingId;
  const isProfileComplete = currentUserMainListingId !== undefined;

  const isOwnListing = currentUserAdminId === currentListing?.attributes?.publicData?.adminId;
  const isViewerInvited = isInvitedToPosting(currentListing, {
    currentUser,
    profileListing,
    mainUserId: currentUserAdminId,
  });

  let authorName = (listingIsJob || listingIsRFP)
    ? (author?.attributes?.profile?.publicData?.customerProfile?.companyName || author?.attributes?.profile?.publicData?.customerProfile?.name || 'Company')
    : author.attributes.profile.displayName;
  let authorProfileListingId = author?.attributes?.profile?.publicData?.profileListingId || author?.attributes?.profile?.publicData?.firstListingId;

  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  let rfpFreeCountPeriodExpired = false;
  let rfpFreeApplicationsCount = profileListing?.attributes?.publicData?.freeRFPApplicationsUsed?.count || 0;
  const rfpFreeApplicationsStartedAt = profileListing?.attributes?.publicData?.freeRFPApplicationsUsed?.startedAt ? new Date(profileListing?.attributes?.publicData?.freeRFPApplicationsUsed?.startedAt) : null;

  // Determine free application limit based on subscription type
  const isMonthlyPlan = subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly';
  const isYearlyPlan = subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Yearly';
  const freeApplicationLimit = isMonthlyPlan ? 1 : 12;

  if (rfpFreeApplicationsStartedAt) {
    const startedDate = rfpFreeApplicationsStartedAt;
    const now = new Date();

    // Calculate difference in milliseconds
    const diffInMs = now - startedDate;

    if (isMonthlyPlan) {
      // For monthly plans: check if 30 days have passed
      const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
      if (diffInDays >= 30) {
        rfpFreeCountPeriodExpired = true;
      }
    } else if (isYearlyPlan) {
      // For yearly plans: check if 1 year has passed
      const diffInYears = diffInMs / (1000 * 60 * 60 * 24 * 365);
      if (diffInYears >= 1) {
        rfpFreeCountPeriodExpired = true;
      }
    }
  }

  if (rfpFreeCountPeriodExpired) {
    rfpFreeApplicationsCount = 0;
  }

  useEffect(() => {
    const givenPersonaRecords = currentListing?.attributes?.personaRecord || [];

    setIsIdentityVerified(hasVerifiedCategory(givenPersonaRecords, 'identity') || hasVerifiedCategory(givenPersonaRecords, 'both'));
    setIsWorkVerified(hasVerifiedCategory(givenPersonaRecords, 'work') || hasVerifiedCategory(givenPersonaRecords, 'both'));
    setIsExperienceVerified(hasVerifiedCategory(givenPersonaRecords, 'experience'));
    setIsEducationVerified(hasVerifiedCategory(givenPersonaRecords, 'education'));

    setIsListingCompanyVerified(isCompanyVerified(givenPersonaRecords));
  }, [currentListing]);

  useEffect(() => {
    const givenPersonaRecords = Array.isArray(currentUserPersonaRecords)
      ? currentUserPersonaRecords
      : [];

    setIsCurrentUserIdentityVerified(hasVerifiedCategory(givenPersonaRecords, 'identity') || hasVerifiedCategory(givenPersonaRecords, 'both'));
    setIsCurrentUserWorkVerified(hasVerifiedCategory(givenPersonaRecords, 'work') || hasVerifiedCategory(givenPersonaRecords, 'both'));
    setIsCurrentUserExperienceVerified(hasVerifiedCategory(givenPersonaRecords, 'experience'));
    setIsCurrentUserEducationVerified(hasVerifiedCategory(givenPersonaRecords, 'education'));

    setIsCurrentUserCompanyVerified(isCompanyVerified(givenPersonaRecords));
  }, [currentUserPersonaRecords]);

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();

  const [applying, setApplying] = useState(false);
  const [localTransactionStatus, setLocalTransactionStatus] = useState(null);
  const [showConsultantModal, setShowConsultantModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [rfpPrice, setRfpPrice] = useState(null);
  const [localAppliedConsultants, setLocalAppliedConsultants] = useState([]);

  // Document consent modal states
  const [showDocConsentModal, setShowDocConsentModal] = useState(false);
  const [docConsentChecked, setDocConsentChecked] = useState(false);
  const [pendingApplyAction, setPendingApplyAction] = useState(null);
  // The upload consent an agency saved before picking a consultant; one application uses it up.
  const [agencyUploadConsentId, setAgencyUploadConsentId] = useState(null);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [applicationResume, setApplicationResume] = useState(null);

  // Document upload states
  const [identityDocuments, setIdentityDocuments] = useState([]);
  const identityDocumentsRef = useRef([]);
  const [workAuthorizationDocuments, setWorkAuthorizationDocuments] = useState([]);
  const workAuthDocumentsRef = useRef([]);


  // Keep refs in sync with state for async access
  const setIdentityDocumentsWithRef = (updater) => {
    setIdentityDocuments(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      identityDocumentsRef.current = next;
      return next;
    });
  };
  const setWorkAuthDocumentsWithRef = (updater) => {
    setWorkAuthorizationDocuments(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      workAuthDocumentsRef.current = next;
      return next;
    });
  };

  // Invite to Job/RFP state
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedJobForInvitation, setSelectedJobForInvitation] = useState(null);
  const [jobRfpListings, setJobRfpListings] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitedTo, setInvitedTo] = useState([]);
  const [listingAdminData, setListingAdminData] = useState(null);

  const dispatch = useDispatch();

  // Visibility conditions for invite button
  const currentUserType = userTypeOf(currentUser);
  const isCustomer = currentUserType === 'customer';
  const canInvite = isCustomer && (listingIsCandidate || listingIsAgency) && !isOwnListing;
  const agencyType = listing?.attributes?.publicData?.agencyProfile?.agencyType || listing?.attributes?.publicData?.agencyType || '';

  // Handle invite button click - fetch data on-demand
  const handleInviteClick = async () => {
    setInviteLoading(true);

    try {
      // Get current user's main account ID
      const currentUserMainId = currentUser?.attributes?.profile?.publicData?.adminId
        || currentUser?.id?.uuid;

      // Fetch customer's job/RFP listings
      const listingsResponse = await getOwnJobRfpListings({
        apiQueryParams: {
          page: 1,
          perPage: 100,
          authorId: currentUserMainId,
          pub_listingType: listingIsCandidate
            ? 'job_listing'
            : ['job_listing', 'rfp_listing'],
          pub_isDraftListing: false,
          status: ['published', 'open'],
        },
        responseFields: ['title', 'state', 'publicData'],
        publicDataFields: ['isDraftListing', 'listingType', 'adminId', 'invitedUsersToPosting', 'appliers'],
      });

      const allListings = listingsResponse?.allListings?.[0]?.data || [];
      const filteredListings = allListings.filter(
        l => !l?.attributes?.publicData?.isDraftListing && l?.attributes?.state !== 'closed'
      );
      setJobRfpListings(filteredListings);

      // Fetch listing admin details; the invitation is addressed by the server from their id
      const adminId = listing?.attributes?.publicData?.adminId
        || listing?.author?.id?.uuid;

      if (adminId) {
        const adminResponse = await getUserDetails({
          userId: adminId,
          responseFields: [
            'profile.displayName',
            'profile.publicData.profileListingId',
            'profile.publicData.firstListingId',
          ],
          imageFields: [],
        });
        setListingAdminData({
          ...adminResponse?.data?.data,
          profileImage: adminResponse?.data?.included?.[0],
        });
      }

      setInviteModalOpen(true);
    } catch (error) {
      // console.error('Error fetching invite data:', error);
      message.error('Failed to load invitation data');
    } finally {
      setInviteLoading(false);
    }
  };

  // Handle sending the invitation
  const handleSendInvitation = async () => {
    if (!selectedJobForInvitation) return;

    setInviteLoading(true);
    try {
      const selectedListing = jobRfpListings.find(
        l => l.id.uuid === selectedJobForInvitation
      );

      const isJobPosting = selectedListing?.attributes?.publicData?.listingType === 'job_listing';
      const isRFPPosting = selectedListing?.attributes?.publicData?.listingType === 'rfp_listing';

      const toEmail = listingAdminData?.attributes?.email;
      const toUserId = listingAdminData?.id?.uuid;
      const invitedUserName = listingAdminData?.attributes?.profile?.displayName;
      const toUserProfileListingId = listingAdminData?.attributes?.profile?.publicData?.profileListingId
        || listingAdminData?.attributes?.profile?.publicData?.firstListingId;

      // Determine user type
      const listingAgencyType = agencyType;
      const toUserType = listingIsCandidate
        ? 'candidate'
        : listingAgencyType === 'Staffing Services'
          ? 'staffingProvider'
          : listingAgencyType === 'Implementation Service'
            ? 'implementationService'
            : listingAgencyType === 'Staffing and Implementation Services'
              ? 'staffingAndImplementationServices'
              : 'candidate';

      await dispatch(sendInvitationToUser(
        selectedJobForInvitation,
        selectedListing?.attributes?.title,
        toEmail,
        toUserId,
        invitedUserName,
        toUserType,
        toUserProfileListingId,
        isJobPosting,
        isRFPPosting
      ));

      setInvitedTo([...invitedTo, selectedJobForInvitation]);
      setInviteModalOpen(false);
      setSelectedJobForInvitation(null);
      message.success('Invitation sent successfully');
    } catch (error) {
      // console.error('Error sending invitation:', error);
      message.error('Failed to send invitation');
    } finally {
      setInviteLoading(false);
    }
  };

  useEffect(() => {
    const updateRFPPrice = () => {
      const subscriptionType = subscriptionStatus?.planType === 'Verified Delivery Vendor Monthly' ? 'Verified delivery vendor RFP bid price'
        : subscriptionStatus?.planType === 'Verified Delivery Vendor Yearly' ? 'Verified delivery vendor RFP bid price'
          : subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly' ? 'Verified plus delivery vendor RFP bid price'
            : 'Verified plus delivery vendor RFP bid price';
      // : subscriptionStatus?.planType === 'verified-plus-delivery-vendor-yearly' ? 'Verified plus delivery vendor yearly plan'

      setRfpPrice(
        subscriptionPrices?.prices?.find(price => price?.product?.name === subscriptionType)
      );
    };
    if (subscriptionPrices && currentUser && subscriptionStatus) {
      if (userTypeOf(currentUser) === 'agency') {
        updateRFPPrice();
      }
    }
  }, [subscriptionPrices, currentUser, subscriptionStatus]);

  const currentUserIsCandidate = userTypeOf(currentUser) === 'candidate';
  const currentUserIsAgency = userTypeOf(currentUser) === 'agency';

  const hasAppliedViaTransaction = Boolean(listingApplyTransaction || localTransactionStatus);

  const currentUserIsCustomer = userTypeOf(currentUser) === 'customer';

  const stripUploadPrefix = value => {
    if (!value || typeof value !== 'string') {
      return '';
    }

    const firstUnderscoreIndex = value.indexOf('_');
    return firstUnderscoreIndex > -1 ? value.slice(firstUnderscoreIndex + 1) : value;
  };

  const profileResume = (() => {
    const profilePublicData = currentUser?.attributes?.profile?.publicData || {};
    const profileListingPublicData = profileListing?.attributes?.publicData || {};
    const resumeUrl = profilePublicData?.resumeUrl || profileListingPublicData?.resumeUrl;

    if (!resumeUrl) {
      return null;
    }

    return {
      url: resumeUrl,
      name: profilePublicData?.resumeName || profileListingPublicData?.resumeName || 'Profile Resume',
      uploadedAt: profilePublicData?.resumeUploadedAt || profileListingPublicData?.resumeUploadedAt || null,
      source: 'profileResume',
    };
  })();

  const selectedResumeForApplication = applicationResume?.url
    ? { ...applicationResume, source: 'applicationUpload' }
    : profileResume;

  const selectedResumeFileName = stripUploadPrefix(selectedResumeForApplication?.name || '');

  const handleApplicationResumeUpload = async file => {
    const uploader = onUploadResumeBlobFile || onUploadBlobFile;
    if (typeof uploader !== 'function') {
      message.error('Resume upload is currently unavailable.');
      return false;
    }

    const allowedTypes = ['.pdf', '.txt'];
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;

    if (!allowedTypes.includes(fileExtension)) {
      message.error('Only PDF and TXT files are allowed for resumes.');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Resume file must be smaller than 5MB.');
      return false;
    }

    setResumeUploading(true);
    try {
      const tempId = `${file.name}_${Date.now()}`;
      const responseData = await uploader({
        id: tempId,
        file,
        userId: currentUser?.id?.uuid,
        candidateListingId: currentUserMainListingId || '',
      });

      if (!responseData?.blobUrl) {
        throw new Error('No resume URL returned');
      }

      setApplicationResume({
        url: responseData.blobUrl,
        name: responseData.fileName || file.name,
        blobName: responseData.blobName || '',
        uploadedAt: new Date().toISOString(),
        source: 'applicationUpload',
      });

      message.success('Resume selected for this application.');
    } catch (error) {
      message.error('Resume upload failed. Please try again.');
    } finally {
      setResumeUploading(false);
    }

    return false;
  };

  const renderResumePopupButton = () => {
    if (!currentUserIsCandidate) {
      return null;
    }

    return (
      <Button
        type="primary"
        size="large"
        onClick={() => setShowResumeModal(true)}
      >
        {applicationResume?.url ? 'Resume Selected' : 'Change Resume'}
      </Button>
    );
  };

  const ensureHttps = (url) => {
    if (!url) return '';
    return url.startsWith('http://') || url.startsWith('https://')
      ? url
      : `https://${url}`;
  };

  const transactionStatus = listingApplyTransaction?.status || localTransactionStatus;
  const status = ['rejected', 'scheduled', 'shortlisted', 'pending', 'candidate consent pending'].includes(transactionStatus)
    ? transactionStatus
    : hasAppliedViaTransaction
      ? 'pending'
      : null;

  const handleApplyWithConsultant = async (listingId, consultantId, pickedRow) => {
    try {
      const isUserInvited = isViewerInvited;

      // The picker lists accepted consultants only, so the picked row is the agency's accepted link.
      const { applyStatus, consultantListingId } = agencyApplyWithConsultant({
        link: { ...pickedRow, status: 'accepted' },
      });

      await onInitiateApplyTransaction(
        listingId,
        isUserInvited,
        consultantId,
        consultantListingId,
        true,
        identityDocuments,
        workAuthorizationDocuments,
        null,
        applyStatus,
        agencyUploadConsentId || undefined
      );

      // Update local state to track this consultant as applied. Do NOT set the
      // listing-level transaction status here — agencies apply per consultant and
      // must keep applying for the remaining consultants.
      setLocalAppliedConsultants(prev => [...prev, consultantListingId]);
      // Reset the uploaded documents and their consent so the next consultant starts fresh.
      setIdentityDocumentsWithRef([]);
      setWorkAuthDocumentsWithRef([]);
      setAgencyUploadConsentId(null);
      setPendingApplyAction(null);
      setDocConsentChecked(false);
      setShowConsultantModal(false);
    } catch (error) {
      if (isConsentRequiredRefusal(error)) {
        // The server did not accept the consent: back to the consent dialog, still ticked, to retry.
        setAgencyUploadConsentId(null);
        setShowConsultantModal(false);
        setShowDocConsentModal(true);
        message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
        return;
      }
      message.error("Failed to send application. Please try again.");
    }
  };

  const handleUnauthenticatedApply = () => {
    if (typeof onApplyAttemptWithoutAuth === 'function') {
      onApplyAttemptWithoutAuth(id, listingType);
    } else {
      const loginPath = createResourceLocatorString('LoginPage', routeConfiguration, {});
      history.push({
        pathname: loginPath,
        state: { from: `${location.pathname}${location.search}${location.hash}` },
      });
    }
  };

  if (listingIsJob) {
    return (
      // <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <Card
        style={{
          margin: "12px 0",
          minHeight: "280px"
        }}
      >
        {/* Header Section */}
        <Row justify="space-between" align="middle">
          <Col className={css.iconLists} >
            <Title level={4} style={{ marginBottom: 4 }}>
              {title}
            </Title>
            {listingPublicId ? (
              <Text className={css.listingPublicId}>
                {listingPublicId}
              </Text>
            ) : null}
            <Space align="center" className={css.spaceVerticalMobile} gapCol={"large"} style={{ columnGap: "15px" }}>
              {authorProfileListingId ? (
                <a href={`/l/${authorProfileListingId}`} target="_blank" rel="noopener noreferrer">
                  <Text type="secondary" className={css.iconLists}>
                    <BankOutlined /> {authorName}
                  </Text>
                </a>
              ) : (
                <Text type="secondary">
                  <BankOutlined /> {authorName}
                </Text>
              )}

              {
                publicData?.createdByDisplayName ?
                  <Text type="secondary">
                    <UserOutlined /> Posted by {publicData?.createdByDisplayName}
                  </Text>
                  : null
              }

              {/* <Text type="success" strong style={{ margin: 0 }}><CheckCircleOutlined /> Verified Buyer</Text> */}
              {
                publicData?.postedAt ?
                  <Text type="secondary"><CalendarOutlined />Posted on {formatDate(publicData?.postedAt)}</Text>
                  : null
              }
            </Space>
          </Col>

          <Col>
            <Space>
              {!isOwnListing &&
                !currentUserIsCustomer &&
                (currentUserIsAgency || !hasAppliedViaTransaction) && (
                <Button
                  type="primary"
                  size="large"
                  onClick={async () => {
                    if (!currentUser) {
                      handleUnauthenticatedApply();
                      return;
                    }

                    if (!isProfileComplete) {
                      history.push({
                        pathname: '/profile-settings',
                        search: `?applyWorkId=${encodeURIComponent(id)}`,
                      });
                      return;
                    }

                    // If current user is agency, show consultant selection modal
                    if (currentUserIsAgency) {
                      if (acceptedConsultantsExist === false) {
                        message.error(NO_ACCEPTED_CONSULTANT);
                        return;
                      }

                      if (!subscriptionStatus || !subscriptionStatus?.hasSubscription || subscriptionStatus?.isExpired) {
                        if (numberOfActiveApplications >= 10) {
                          message.error("You should have an active subscription to have more than 10 active job applications as a supplier.");
                          return;
                        }
                      }

                      // Documents go with the consultant's application only after the agency's
                      // own upload consent; the picker opens once it is saved.
                      if (identityDocumentsRef.current.length > 0 || workAuthDocumentsRef.current.length > 0) {
                        setAgencyUploadConsentId(null);
                        setDocConsentChecked(false);
                        setPendingApplyAction({ forAgency: true });
                        setShowDocConsentModal(true);
                        return;
                      }

                      setShowConsultantModal(true);
                      return;
                    }

                    // Existing logic for candidates
                    // const currentUserSkills = currentUser?.attributes?.profile?.publicData?.skills || [];
                    // const requiredVerificationsJob = currentListing?.attributes?.publicData?.verificationRequirementsJob || {};

                    // if (Array.isArray(requiredVerificationsJob) && requiredVerificationsJob.length > 0 && !requiredVerificationsJob.includes('None')) {

                    //   const normalize = str => str.toLowerCase();

                    //   const missingVerifications = requiredVerificationsJob.filter(reqVerif => {
                    //     const req = normalize(reqVerif);

                    //     // if (req.includes('education') && isCurrentUserEducationVerified) return false;

                    //     if (req.includes('work authorization') && isCurrentUserWorkVerified) return false;
                    //     // if (req.includes('work experience') && isCurrentUserExperienceVerified) return false;

                    //     if (req.includes('identity') && isCurrentUserIdentityVerified) return false;

                    //     return true;
                    //   });

                    //   if (missingVerifications.length > 0) {
                    //     message.error(`You are missing the following required verifications to apply: ${missingVerifications.join(', ')}`);
                    //     return;
                    //   }
                    // }

                    // Check if ALL required skills are present in the user's skills with required or higher experience
                    // const hasAllRequiredSkills = currentListing?.attributes?.publicData?.requiredSkillsJob?.every(jobListingSkill => {
                    //   const matchedSkill = currentUserSkills.find(currentUserSkill =>
                    //     currentUserSkill.name?.toLowerCase() === jobListingSkill.name?.toLowerCase()
                    //   );

                    //   return matchedSkill && Number(matchedSkill.skillExp) >= Number(jobListingSkill.skillExp);
                    // });

                    // if (currentUser?.attributes?.profile?.publicData?.userType === 'candidate' && !hasAllRequiredSkills) {
                    //   message.error("You do not meet all the required skills for this job.");
                    //   return;
                    // }

                    const resumeToUse = selectedResumeForApplication;
                    if (currentUserIsCandidate && !resumeToUse?.url) {
                      message.error('A resume is required to apply. Upload a resume for this application or add one to your profile.');
                      setShowResumeModal(true);
                      return;
                    }

                    // If documents uploaded, require consent before applying
                    const hasDocuments = identityDocumentsRef.current.length > 0 || workAuthDocumentsRef.current.length > 0;
                    if (hasDocuments) {
                      const isUserInvited = isViewerInvited;
                      setDocConsentChecked(false);
                      setPendingApplyAction({ isUserInvited, selectedResume: resumeToUse });
                      setShowDocConsentModal(true);
                      return;
                    }

                    setApplying(true);

                    const isUserInvited = isViewerInvited;

                    await onInitiateApplyTransaction(
                      currentListing?.id?.uuid,
                      isUserInvited,
                      '',
                      '',
                      false,
                      identityDocuments,
                      workAuthorizationDocuments,
                      resumeToUse,
                      'pending'
                    );
                    setApplying(false);
                    setLocalTransactionStatus('pending');
                    message.success("Application sent successfully!");
                  }}
                >
                  {applying ? <Spinner rootClassName={css.spinnerWhite} /> :
                    currentUserIsAgency ? 'Apply' :
                      (isViewerInvited ? 'Accept invitation' : 'Apply Now')
                  }
                </Button>
              )}
              {!isOwnListing && !currentUserIsCustomer && !currentUserIsAgency && status === 'pending' && (
                <Tag color="green" icon={<CheckOutlined />} onClick={() => { return; }} style={{ marginBottom: 0 }}>
                  {isViewerInvited ? 'Invitation accepted' : 'Applied'}
                </Tag>
              )}
              {!isOwnListing && !currentUserIsCustomer && !currentUserIsAgency && status === 'shortlisted' && (
                <Tag color="blue" icon={<UnorderedListOutlined />} onClick={() => { return; }} style={{ marginBottom: 0 }}>
                  Shortlisted
                </Tag>
              )}

              {!isOwnListing && !currentUserIsCustomer && !currentUserIsAgency && status === 'scheduled' && (
                <Tag color="purple" icon={<CalendarOutlined />} onClick={() => { return; }} style={{ marginBottom: 0 }}>
                  Interview Scheduled
                </Tag>
              )}

              {!isOwnListing && !currentUserIsCustomer && !currentUserIsAgency && status === 'rejected' && (
                <Tag color="red" icon={<CloseCircleOutlined />} onClick={() => { return; }} style={{ marginBottom: 0 }}>
                  Rejected
                </Tag>
              )}

              {!isOwnListing && !currentUserIsCustomer && !currentUserIsAgency && status === 'candidate consent pending' && (
                <Tag color="orange" onClick={() => { return; }} style={{ marginBottom: 0 }}>
                  Consent Pending
                </Tag>
              )}

              <Button
                size="large"
                onClick={() => {
                  history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id: id, slug: slug }, {}));
                }}
              >
                View Job
              </Button>
            </Space>
          </Col>
        </Row>

        <Divider />
        {/* Job Info */}
        <Row gutter={[16, 8]} className={css.iconLists}>
          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text>
                <EnvironmentOutlined /> {Array.isArray(publicData?.workSiteTypeJob) && publicData?.workSiteTypeJob?.length > 0 ? ((publicData?.workSiteTypeJob?.includes('On-site') || publicData?.workSiteTypeJob?.includes('Hybrid')) ? `${publicData?.jobLocationJob?.selectedPlace?.address || 'Location not specified'} • ${publicData?.workSiteTypeJob?.join(', ')}` : `${publicData?.workSiteTypeJob?.join(', ')}`) : 'Not specified'}
              </Text>
              <Text>
                <ClockCircleOutlined /> Engagement duration • {publicData?.engagementDurationJob || 'Not specified'}
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text>
                {/* <SolutionOutlined /> Contract • {publicData?.contractTypeJob === 'Subcontract (Select if not direct)' ? 'Subcontract' : publicData?.contractTypeJob} */}
                <SolutionOutlined /> Employment type • {publicData?.employmentTypeJob || 'Not specified'}
              </Text>
              <Text>
                <SafetyCertificateOutlined /> Work Authorization: {Array.isArray(publicData?.workAuthReqJob) && publicData?.workAuthReqJob?.length > 0 ? publicData?.workAuthReqJob?.join(', ') : (publicData?.workAuthReqJob || 'Not specified')}
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text><DollarOutlined /> {(publicData?.billRateMinJob && publicData?.billRateMaxJob) ? `${publicData?.billRateMinJob} - ${publicData?.billRateMaxJob} / hr` : 'Not disclosed'}</Text>
              {/* Giving billrate usd/hour */}
              <Text>
                <SolutionOutlined /> Engagement Type • {Array.isArray(publicData?.workEngagementTypeJob) && publicData?.workEngagementTypeJob?.length > 0 ? publicData?.workEngagementTypeJob?.join(', ') : 'Not specified'}
              </Text>
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: "8px 0" }} />
        {/* Tags */}
        <Space size="small" wrap style={{ marginTop: 16 }}>
          {publicData?.experienceLevelJob && <Tag>{publicData?.experienceLevelJob}</Tag>}
          {Array.isArray(publicData?.requiredSkillsJob) && publicData?.requiredSkillsJob?.length > 0 ? (
            <div>
              {publicData.requiredSkillsJob.map((skill, index) => (
                <Tag key={index}>
                  {skill.name} (Exp: {skill.skillExp} {skill.skillExp <= "1" ? "year" : "years"})
                </Tag>
              ))}
            </div>
          ) : (
            <Tag>No specific skills required</Tag>
          )}
          {/* <Tag>Migration</Tag>
            <Tag>Remote Eligible</Tag> */}
        </Space>


        <Divider style={{ margin: "8px 0" }} />
        {/* Verifications */}
        {(
            (!Array.isArray(jobRequirements) ||
              jobRequirements.length === 0 ||
              jobRequirements.includes('None')) ? (
              <Row justify="space-between" align="middle" style={{ marginTop: 20, flexFlow: "nowrap" }}>
                <Col>
                  <div>
                    <Text strong>No Integrity Checks needed to Apply</Text>
                    <br />
                    <Space size="middle" wrap style={{ marginTop: 8 }}>
                      <Tag color="green" icon={<CheckCircleOutlined />}>
                        None
                      </Tag>
                    </Space>
                  </div>
                </Col>
                <Col>
                  {!isOwnListing && !currentUserIsCustomer && isProfileComplete && (currentUserIsAgency || !hasAppliedViaTransaction) && (
                    <Space>
                      <UploadDocumentButton
                        modalTitle="Upload Identity Documents"
                        description="Driver's License, State ID, Passport, Birth Certificate, etc."
                        buttonLabel="Upload ID"
                        uploadedLabel="ID Uploaded"
                        documents={identityDocuments}
                        setDocumentsWithRef={setIdentityDocumentsWithRef}
                        onUploadBlobFile={onUploadBlobFile}
                        buttonType="primary"
                        buttonSize="large"
                      />
                      <UploadDocumentButton
                        modalTitle="Upload Work Authorization Documents"
                        description="EAD Card, Visa, I-94, Social Security Card, etc."
                        buttonLabel="Upload Work Docs"
                        uploadedLabel="Work Docs Uploaded"
                        documents={workAuthorizationDocuments}
                        setDocumentsWithRef={setWorkAuthDocumentsWithRef}
                        onUploadBlobFile={onUploadBlobFile}
                        buttonType="primary"
                        buttonSize="large"
                      />
                      {renderResumePopupButton()}
                    </Space>
                  )}
                </Col>
              </Row>
            ) :

              <Row wrap={false} justify="space-between" align="middle" style={{ marginTop: 20 }}>
                <Col>
                  <div>
                    <Text strong>Integrity Checks needed to Apply:</Text><br />
                    <Space size="middle" wrap style={{ marginTop: 8, rowGap: 5 }}>
                      {jobRequirements.map((verification, index) => (
                        <Tag key={index} color="green" icon={<IdcardOutlined />}>
                          {verification}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </Col>
                <Col>
                  {!isOwnListing && !currentUserIsCustomer && isProfileComplete && (currentUserIsAgency || !hasAppliedViaTransaction) && (
                    <Space>
                      <UploadDocumentButton
                        modalTitle="Upload Identity Documents"
                        description="Driver's License, State ID, Passport, Birth Certificate, etc."
                        buttonLabel="Upload ID"
                        uploadedLabel="ID Uploaded"
                        documents={identityDocuments}
                        setDocumentsWithRef={setIdentityDocumentsWithRef}
                        onUploadBlobFile={onUploadBlobFile}
                        buttonType="primary"
                        buttonSize="large"
                      />
                      <UploadDocumentButton
                        modalTitle="Upload Work Authorization Documents"
                        description="EAD Card, Visa, I-94, Social Security Card, etc."
                        buttonLabel="Upload Work Docs"
                        uploadedLabel="Work Docs Uploaded"
                        documents={workAuthorizationDocuments}
                        setDocumentsWithRef={setWorkAuthDocumentsWithRef}
                        onUploadBlobFile={onUploadBlobFile}
                        buttonType="primary"
                        buttonSize="large"
                      />
                      {renderResumePopupButton()}
                    </Space>
                  )}
                </Col>
              </Row>
          )}

        <ConsultantSelectionModal
          isOpen={showConsultantModal}
          onClose={() => setShowConsultantModal(false)}
          listing={currentListing}
          onApplyWithConsultant={handleApplyWithConsultant}
          localAppliedConsultants={localAppliedConsultants}
          onSearch={onSearchAcceptedConsultants}
        />

        <ApplicationResumeModal
          isOpen={showResumeModal}
          onClose={() => setShowResumeModal(false)}
          selectedResume={selectedResumeForApplication}
          resumeDisplayName={selectedResumeFileName}
          hasUploadedResume={!!applicationResume?.url}
          resumeUploading={resumeUploading}
          onUploadResume={handleApplicationResumeUpload}
          onUseProfileResume={() => setApplicationResume(null)}
        />

        {/* Document upload consent modal */}
        <Modal
          open={showDocConsentModal}
          title="Document Upload Consent"
          onCancel={() => {
            setShowDocConsentModal(false);
            setPendingApplyAction(null);
            setDocConsentChecked(false);
          }}
          footer={[
            <Button
              key="cancel"
              onClick={() => {
                setShowDocConsentModal(false);
                setPendingApplyAction(null);
                setDocConsentChecked(false);
              }}
            >
              Cancel
            </Button>,
            <Button
              key="apply"
              type="primary"
              disabled={!docConsentChecked}
              loading={applying}
              onClick={async () => {
                if (!docConsentChecked) {
                  message.error('You must provide consent to proceed with the uploaded documents.');
                  return;
                }
                if (currentUserIsCandidate && !pendingApplyAction?.selectedResume?.url) {
                  message.error('A resume is required to apply. Upload a resume for this application or add one to your profile.');
                  setShowResumeModal(true);
                  return;
                }
                setApplying(true);
                const forAgency = !!pendingApplyAction?.forAgency;
                // The documents go with the application only once the consent is saved; otherwise
                // the dialog stays open, still ticked, so the person can try again. An agency picks
                // the consultant afterwards, so its consent names only the job.
                let consentId;
                try {
                  consentId = await saveConsentFor('IdWorkAuthDocUpload', {
                    candidateListingId: forAgency
                      ? undefined
                      : currentUser?.attributes?.profile?.publicData?.profileListingId ||
                        currentUser?.attributes?.profile?.publicData?.firstListingId,
                    jobListingId: currentListing?.id?.uuid,
                  });
                } catch (error) {
                  setApplying(false);
                  if (!(error instanceof ConsentSaveFailedError)) throw error;
                  message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
                  return;
                }
                setShowDocConsentModal(false);
                if (forAgency) {
                  setAgencyUploadConsentId(consentId);
                  setApplying(false);
                  setShowConsultantModal(true);
                  return;
                }
                try {
                  await onInitiateApplyTransaction(
                    currentListing?.id?.uuid,
                    pendingApplyAction?.isUserInvited,
                    '',
                    '',
                    false,
                    identityDocuments,
                    workAuthorizationDocuments,
                    pendingApplyAction?.selectedResume,
                    'pending',
                    consentId
                  );
                  setLocalTransactionStatus('pending');
                  message.success('Application sent successfully!');
                } catch (error) {
                  if (isConsentRequiredRefusal(error)) {
                    // The server did not accept the consent: reopen the dialog, still ticked, to retry.
                    setApplying(false);
                    setShowDocConsentModal(true);
                    message.error(intl.formatMessage({ id: CONSENT_SAVE_FAILED_MESSAGE_ID }));
                    return;
                  }
                  setApplying(false);
                  setPendingApplyAction(null);
                  setDocConsentChecked(false);
                  throw error;
                }
                setApplying(false);
                setPendingApplyAction(null);
                setDocConsentChecked(false);
              }}
            >
              {pendingApplyAction?.forAgency ? 'Agree & Continue' : 'Agree & Apply'}
            </Button>,
          ]}
        >
          <div className={css.docConsentCheckbox}>
            <Checkbox
              checked={docConsentChecked}
              onChange={e => setDocConsentChecked(e.target.checked)}
            >
              I consent to the uploaded identity and work authorization documents being accessed
              and reviewed by the job poster and authorized platform administrators for the
              purpose of evaluating my job application.
              <span className={css.docConsentRequired}>*</span>
            </Checkbox>
          </div>
        </Modal>

      </Card>

      // {/* Consultant Selection Modal for Agencies */}

      // </NamedLink>
    );
  }

  if (listingIsRFP) {
    return (
      // <NamedLink className={classes} name="ListingPage" params={{ id, slug }}>
      <Card
        style={{
          margin: "12px 0",
          minHeight: "320px"
        }}
      >
        {/* Header */}
        <Row justify="space-between" align="middle">
          <Col flex="auto">
            {/* <Avatar
                size={64}
                src={
                  <LazyImage
                    rootClassName={isPost ? css.rootForImagePost : css.rootForImage}
                    alt={title}
                    image={firstImage}
                    variants={variants}
                    sizes={renderSizes}
                  />
                }
              /> */}
            <Title level={4} style={{ marginBottom: 4 }}>
              {richText(title, {
                longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
                longWordClass: css.longWord,
              })}
            </Title>
            {listingPublicId ? (
              <Text className={css.listingPublicId}>
                {listingPublicId}
              </Text>
            ) : null}
            {/* <Text type="secondary">
                {showAuthorInfo ? (
                  <span>
                    <FormattedMessage id="ListingCard.author" values={{ authorName }} />
                  </span>
                ) : null}
              </Text><br /> */}
            <Space size="small" align="center">
              {authorProfileListingId ? (
                <a href={`/l/${authorProfileListingId}`} target="_blank" rel="noopener noreferrer">
                  <Text type="secondary" className={css.iconLists}><BankOutlined /><FormattedMessage id="ListingCard.author" values={{ authorName }} /></Text>
                </a>
              ) : (
                <Text type="secondary" className={css.iconLists}><BankOutlined /><FormattedMessage id="ListingCard.author" values={{ authorName }} /></Text>
              )}
              {/* <Text type="secondary" className={css.iconLists}><BankOutlined /> Texas Department of Information Resources</Text> */}
              {/* <Text type="success" strong style={{ margin: 0 }}><CheckCircleOutlined /> Verified Buyer</Text> */}
              {/* <Tag color="blue" style={{ margin: 0 }}>Open</Tag> */}
            </Space>
          </Col>
          <Col>
            <Space>
              {!isOwnListing && !currentUserIsCustomer && (currentUserIsAgency || !hasAppliedViaTransaction) && (
                <Button
                  type="primary"
                  size="large"
                  onClick={() => {
                    if (!currentUser) {
                      handleUnauthenticatedApply();
                      return;
                    }

                    if (!subscriptionStatus || !subscriptionStatus?.hasSubscription || subscriptionStatus?.isExpired) {
                      message.error("You should have an active subscription to apply to RFPs.");
                      return;
                    }

                    const requiredVerificationsRFP = currentListing?.attributes?.publicData?.requiredVerificationsRFP || {};
                    const requiredSecurityCertificationsRFP = currentListing?.attributes?.publicData?.securityCertificationsRFP || [];
                    const requiredDiversityCertificationsRFP = currentListing?.attributes?.publicData?.diversityCertificationsRFP || [];
                    const requiredVendorCertificationsRFP = currentListing?.attributes?.publicData?.certificationsStandardsRFP || [];

                    // const securityAndComplianceStandards = currentUser?.attributes?.profile?.publicData?.securityAndComplianceStandards || [];
                    // const certifications = currentUser?.attributes?.profile?.publicData?.certifications || [];
                    // const certificationsOrDesignations = currentUser?.attributes?.profile?.publicData?.onBoardingInfo?.certificationsOrDesignations || [];

                    const securityAndComplianceStandards = profileListing?.attributes?.publicData?.securityAndComplianceStandards || [];
                    const certifications = profileListing?.attributes?.publicData?.certifications || [];
                    const certificationsOrDesignations = profileListing?.attributes?.publicData?.onBoardingInfo?.certificationsOrDesignations || [];

                    // Combine required fields
                    const requiredAll = [
                      ...requiredSecurityCertificationsRFP,
                      ...requiredDiversityCertificationsRFP,
                      ...requiredVendorCertificationsRFP
                    ];

                    // Combine user's fields
                    const userAll = [
                      ...securityAndComplianceStandards,
                      ...certifications,
                      ...certificationsOrDesignations
                    ];

                    // Check if all required values are present in the user's combined array
                    const hasAllRequiredCerts = requiredAll.every(requiredValue =>
                      userAll.includes(requiredValue)
                    );

                    if (!hasAllRequiredCerts) {
                      message.error("You do not meet all the required certifications.");
                      return;
                    }

                    // Check if user has free applications remaining
                    if (subscriptionStatus?.hasSubscription && !subscriptionStatus?.expired && (rfpFreeApplicationsCount < freeApplicationLimit) && (subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Yearly' || subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly')) {
                      // Apply directly without payment
                      setApplying(true);
                      // This button reads "Accept invitation" exactly when the viewer is invited.
                      onInitiateApplyTransaction(currentListing?.id?.uuid, isViewerInvited, '', '', false, [], [], null, 'pending')
                        .then(() => {
                          setApplying(false);
                          setLocalTransactionStatus('pending');
                          const periodText = isMonthlyPlan ? 'this month' : 'this year';
                          const updatedCount = rfpFreeApplicationsCount + 1;
                          message.success(`Application sent successfully! You have used ${updatedCount} out of ${freeApplicationLimit} free application${freeApplicationLimit > 1 ? 's' : ''} ${periodText}.`);
                        })
                        .catch((error) => {
                          setApplying(false);
                          message.error("Failed to send application. Please try again.");
                        });
                    } else {
                      // Show payment modal
                      setShowPaymentModal(true);
                    }
                  }}
                >
                  {applying ? <Spinner rootClassName={css.spinnerWhite} /> : isViewerInvited ? 'Accept invitation' : 'Apply Now'}
                </Button>
              )}
              {!isOwnListing && !currentUserIsCustomer && status === 'pending' && (
                <Button type="primary" size="large" onClick={() => { return; }} >
                  {isViewerInvited ? 'Invitation accepted' : 'Applied'}
                </Button>
              )}
              {!isOwnListing && !currentUserIsCustomer && status === 'shortlisted' && (
                <Button type="primary" size="large" onClick={() => { return; }}>
                  Shortlisted
                </Button>
              )}

              {!isOwnListing && !currentUserIsCustomer && status === 'scheduled' && (
                <Button style={{ backgroundColor: 'rgba(83, 94, 255, 0.72)' }} size="large" onClick={() => { return; }}>
                  Interview Scheduled
                </Button>
              )}

              {!isOwnListing && !currentUserIsCustomer && status === 'rejected' && (
                <Button danger size="large" onClick={() => { return; }}>
                  Rejected
                </Button>
              )}
              {!isOwnListing && !currentUserIsCustomer && status === 'candidate consent pending' && (
                <Button size="large" style={{ backgroundColor: '#fa8c16', borderColor: '#fa8c16', color: '#fff' }} onClick={() => { return; }}>
                  Consent Pending
                </Button>
              )}
              <Button
                size='large'
                onClick={() => {
                  history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id: id, slug: slug }, {}));
                }}
              >
                View RFP
              </Button>
            </Space>
          </Col>
        </Row>
        <Divider />

        {/* Free Applications Indicator for Verified Plus Delivery Vendor */}

        {/* Details Section */}
        <Row gutter={[16, 8]} className={css.iconLists}>
          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text>
                <CalendarOutlined /> <strong>Posted:</strong> {publicData?.postedAt || 'Not specified'}
              </Text>
              <Text>
                <FileTextOutlined />{" "}
                <strong>Category:</strong> {publicData?.categoryRFP || 'Not specified'}
              </Text>
              <Text>
                <SafetyCertificateOutlined />{" "}
                <strong>Technology Focus:</strong> {Array.isArray(publicData?.projTechFocusSpecNeedsRFP) && publicData?.projTechFocusSpecNeedsRFP?.length > 0 ? publicData?.projTechFocusSpecNeedsRFP?.join(', ') : 'Not specified'}
              </Text>
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text>
                <ClockCircleOutlined /> <strong>Due:</strong> {publicData?.submissionDeadlineRFP ? new Date(publicData.submissionDeadlineRFP).toLocaleDateString('en-US', {
                  month: 'short',
                  day: '2-digit',
                  year: 'numeric',
                }) : 'Not specified'} {publicData?.submissionDeadlineRFP && <><Space /> CST</>}
              </Text>
              <Text>
                <TeamOutlined /> <strong>Org Type:</strong> {publicData?.issuingOrgTypeRFP || 'Not specified'}
              </Text>
            </Space>
          </Col>

          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text>
                <TrophyOutlined />{" "}
                <strong>Award:</strong> {publicData?.singleMultiAwardIntentRFP || 'Not specified'}
              </Text>
              <Text>
                <OneToOneOutlined />{" "}
                <strong>Service Domains:</strong> {Array.isArray(publicData?.ProjectServiceDomainRFP) && publicData?.ProjectServiceDomainRFP?.length > 0 ? publicData?.ProjectServiceDomainRFP?.join(', ') : 'Not specified'}
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={8}>
            <Space size="middle" direction="vertical" wrap>
              <Text>
                <ClockCircleOutlined />{" "}
                <strong>Contract term/duration:</strong> {publicData?.contractTermDurationRFP || 'Not specified'}
              </Text>
              {/* <Text>
                  <ClockCircleOutlined />{" "}
                  <strong>Incumbent provider:</strong> {publicData?.incumbentProviderRFP}
                </Text> */}
            </Space>
          </Col>
        </Row>

        <Divider style={{ margin: "8px 0" }} />

        {/* Tags */}
        {/* <Space wrap>
            <Tag color="blue">{publicData?.contractTermDurationRFP}</Tag>
          </Space>

          <Divider style={{ margin: "8px 0" }} /> */}

        {/* Verification Section */}

        {Array.isArray(publicData?.requiredVerificationsRFP) &&
          publicData.requiredVerificationsRFP.length > 0 && (
            <div style={{ marginTop: 20 }}>
              <Text strong>Integrity Checks needed to Apply:</Text><br />
              <Space size="middle" wrap style={{ marginTop: 8 }}>
                {publicData.requiredVerificationsRFP.map((verification, index) => (
                  <Tag key={index} color="green" icon={<IdcardOutlined />}>
                    {verification}
                  </Tag>
                ))}
              </Space>
            </div>
          )}

        {/* Payment Confirmation Modal */}
        <Modal
          title={
            (subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly' ||
              subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Yearly') &&
              rfpFreeApplicationsCount >= freeApplicationLimit
              ? "Free Applications Limit Reached"
              : "Payment Required"
          }
          open={showPaymentModal}
          onOk={() => {
            setShowPaymentModal(false);
            if (typeof localStorage !== 'undefined') {
              localStorage.setItem('rfpId', currentListing?.id?.uuid);
            }
            onPayForRFP(rfpPrice?.id, 1, 'rfpApplication', currentListing?.id?.uuid);
          }}
          onCancel={() => setShowPaymentModal(false)}
          okText={`Pay ${formatPrice(rfpPrice)}`}
          cancelText="Cancel"
          width={400}
        >
          {(subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Monthly' ||
            subscriptionStatus?.planType === 'Verified Plus Delivery Vendor Yearly') ? (
            rfpFreeApplicationsCount >= freeApplicationLimit ? (
              <>
                <p>You have used all <strong>{freeApplicationLimit} free RFP application{freeApplicationLimit > 1 ? 's' : ''}</strong> included in your {isMonthlyPlan ? 'monthly' : 'yearly'} subscription.</p>
                <p>To apply to more RFPs, you need to pay <strong>{formatPrice(rfpPrice)}</strong> per application.</p>
                <p>Would you like to proceed with the payment?</p>
              </>
            ) : (
              <>
                <p>Your subscription includes <strong>{freeApplicationLimit} free RFP application{freeApplicationLimit > 1 ? 's' : ''}</strong> per {isMonthlyPlan ? 'month' : 'year'}.</p>
                <p>You have used <strong>{rfpFreeApplicationsCount}</strong> out of {freeApplicationLimit} free application{freeApplicationLimit > 1 ? 's' : ''}.</p>
                <p>You can apply to <strong>{freeApplicationLimit - rfpFreeApplicationsCount}</strong> more RFP{(freeApplicationLimit - rfpFreeApplicationsCount) > 1 ? 's' : ''} for free.</p>
                <p>Would you like to pay <strong>{formatPrice(rfpPrice)}</strong> instead of using a free application?</p>
              </>
            )
          ) : (
            <>
              <p>You need to pay <strong>{formatPrice(rfpPrice)}</strong> to apply to this RFP.</p>
              <p>Would you like to proceed with the payment?</p>
            </>
          )}
        </Modal>
      </Card>
      // </NamedLink>
    );
  }

  const [expanded, setExpanded] = useState({
    staffing: false,
    tech: false,
    compliance: false,
    engagement: false,
  });

  const toggleSection = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getCityStateCountry = (addressString) => {
    // Return an empty string if the address is not valid
    if (!addressString || typeof addressString !== 'string') {
      return "";
    }

    // 1. Remove extra information like '· Remote, Hybrid'
    const cleanedAddress = addressString.split('·')[0];

    // 2. Split the address into parts by the comma, and trim whitespace from each part.
    // Also, filter out any empty parts that might result from extra commas.
    const parts = cleanedAddress.split(',').map(part => part.trim()).filter(Boolean);

    // 3. Check if there are at least 3 parts (for city, state, country)
    if (parts.length < 3) {
      // If not, return the string as is, as we can't reliably determine the city/state.
      return parts.join(', ');
    }

    // 4. Get the last three parts of the array
    const locationParts = parts.slice(-3); // e.g., ['Ballari', 'Karnataka', 'India']

    // 5. Join them back into a single string
    return locationParts.join(', ');
  };

  if (listingIsCandidate) {
    const publicData = currentListing?.attributes?.publicData;
    const linkedinLink = publicData?.onBoardingInfo?.linkedInCandidateLink || "";
    const profileImageVariants = author?.profileImage?.attributes?.variants || {};
    const listingImage = profileImageVariants['square-small2x']?.url || profileImageVariants['square-small']?.url;

    return (
      <>
        <Card
          style={{
            margin: "12px 0",
            minHeight: "350px"
          }}
        >
          {/* Header */}
          <Row justify="space-between">
            <Col>
              <Space align="center">
                {/* <Avatar
                  size={64}
                  icon={<UserOutlined />}
                  style={{
                    backgroundColor: "#f0f2f5",
                    border: "1px solid #e0e0e0",
                  }}
                /> */}
                {listingImage ? (
                  <Avatar
                    size={64}
                    src={listingImage}
                    style={{
                      backgroundColor: "#f0f2f5",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                ) : (
                  <Avatar
                    size={64}
                    style={{
                      backgroundColor: "#f0f2f5",
                      border: "1px solid #e0e0e0",
                      color: 'grey'
                    }}
                  >
                    {currentListing?.attributes?.title ? currentListing?.attributes?.title.charAt(0).toUpperCase() : '?'}
                  </Avatar>
                )}
                <div>

                  <Space size="small" align="center" className={css.spaceVerticalMobile}>
                    <Title level={4} style={{ marginBottom: 4 }}>
                      {currentListing?.attributes?.title}
                    </Title>
                    {/* <Text type="success" strong style={{ margin: 0 }}><CheckCircleOutlined /> Verified</Text> */}
                    {linkedinLink !== '' && (
                      <a
                        href={ensureHttps(linkedinLink)}
                        target="_blank"
                        rel="noreferrer"
                        style={{ marginLeft: 8, fontSize: 14, fontWeight: "normal" }}
                      >
                        <span style={{ color: "#0A66C2" }}><LinkedinOutlined /> LinkedIn</span>
                      </a>
                    )}
                  </Space><br />
                  <Text type="secondary">
                    {publicData?.onBoardingInfo?.candidateHeading || 'No headline provided'}
                  </Text>
                </div>
              </Space>
            </Col>

            {/* Right Side Tags */}
            {/* <Col>
              <Space wrap>
                {isIdentityVerified && (
                  <Tag color="green">
                    <IdcardOutlined /> ID
                  </Tag>
                )}
                {isWorkVerified && (
                  <Tag color="green">
                    <SolutionOutlined /> Work
                  </Tag>
                )}
                {isEducationVerified &&
                  <Tag color="green">
                    <ClockCircleOutlined /> Education
                  </Tag>
                }
                {isExperienceVerified &&
                  <Tag color="green">
                    <BookOutlined /> Experience
                  </Tag>
                }
              </Space>
            </Col> */}
          </Row>

          <Divider />

          {/* Summary Info */}
          <Row gutter={[24, 12]} className={css.iconLists}>
            <Col xs={24} sm={12} md={8}>
              <Space align="start">
                <FileDoneOutlined />
                <div>
                  {Array.isArray(publicData?.skills) && publicData?.skills?.length > 0 ? (
                    publicData.skills
                      .sort((a, b) => b.skillExp - a.skillExp) // sort descending by experience
                      .slice(0, 1) // show only top 1 key skill
                      .map((skill, index) => (
                        <div key={index}>
                          <Text>
                            {skill.skillExp} {skill.skillExp > 1 ? 'years' : 'year'} in{' '}
                            {skill.name?.charAt(0).toUpperCase() + skill.name?.slice(1)}
                          </Text>
                        </div>
                      ))
                  ) : (
                    <Text>No skills listed</Text>
                  )}
                </div>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <PicCenterOutlined />
                <Text>
                  Primary Domain:{' '}
                  {Array.isArray(publicData?.skills) && publicData?.skills.length > 0
                    ? publicData.skills
                      .reduce((max, skill) => (skill.skillExp > max.skillExp ? skill : max), publicData.skills[0])
                      .name.charAt(0).toUpperCase() +
                    publicData.skills
                      .reduce((max, skill) => (skill.skillExp > max.skillExp ? skill : max), publicData.skills[0])
                      .name.slice(1)
                    : 'N/A'}
                </Text>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <LaptopOutlined />
                <Text>
                  Technology Focus:{' '}
                  {Array.isArray(publicData?.skills) && publicData?.skills.length > 0
                    ? publicData.skills
                      .sort((a, b) => b.skillExp - a.skillExp)
                      .slice(0, 5)
                      .map(skill => skill.name?.charAt(0).toUpperCase() + skill.name?.slice(1))
                      .join(', ')
                    : 'N/A'}
                </Text>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <EnvironmentOutlined />
                <Text>
                  {publicData?.onBoardingInfo?.preferredWorkLocation?.selectedPlace?.address
                    ? getCityStateCountry(publicData?.onBoardingInfo?.preferredWorkLocation?.selectedPlace?.address)
                    : 'Location not specified'}
                  {' · '}
                  {Array.isArray(publicData?.onBoardingInfo?.workSite) && publicData?.onBoardingInfo?.workSite?.length > 0
                    ? publicData?.onBoardingInfo?.workSite?.join(', ')
                    : 'Work site not specified'}
                </Text>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <TeamOutlined />
                <Text>
                  Employment: {Array.isArray(publicData?.onBoardingInfo?.workType) && publicData?.onBoardingInfo?.workType?.length > 0
                    ? publicData?.onBoardingInfo?.workType?.join('/')
                    : 'Not specified'}
                </Text>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <EnvironmentOutlined />
                <Text>Relocation: {publicData?.onBoardingInfo?.willingToRelocate === 'Yes' ? 'Open' : publicData?.onBoardingInfo?.willingToRelocate === 'No' ? 'Not open' : 'Not specified'}</Text>
              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <DollarOutlined />
                <Text>
                  <Text>
                    {(() => {
                      const min = parseFloat(publicData?.onBoardingInfo?.minBillingRateExpectation?.replace(/[^0-9.]/g, '') || 0);
                      const target = parseFloat(publicData?.onBoardingInfo?.billingRateTarget?.replace(/[^0-9.]/g, '') || 0);

                      if (!min && !target) return 'Rate: N/A';

                      const low = Math.min(min, target);
                      const high = Math.max(min, target);

                      return `Rate: $${low}–$${high}/hr (target)`;
                    })()}
                  </Text>
                </Text>

              </Space>
            </Col>

            <Col xs={24} sm={12} md={8}>
              <Space>
                <ClockCircleOutlined />
                <Text>
                  Availability:{' '}
                  {(() => {
                    const dateStr = publicData?.onBoardingInfo?.openToWorkDate;
                    if (!dateStr) return 'N/A';

                    const date = new Date(dateStr);
                    const today = new Date();
                    today.setHours(0, 0, 0, 0); // remove time for accurate comparison
                    date.setHours(0, 0, 0, 0);

                    if (isNaN(date.getTime())) return 'Invalid Date';

                    return date <= today
                      ? 'Immediately'
                      : date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                  })()}
                </Text>
              </Space>
            </Col>
          </Row>

          <div
            style={{
              marginTop: 12,
              background: "#fafafa",
              borderRadius: 8,
              padding: "8px 12px",
              border: "1px solid #f0f0f0",
              display: "inline-block",
            }}
          >
            <Text strong>Work Authorization:</Text>{" "}
            <Text>{publicData?.identityAndWorkAuthorizationDetails?.workAuthCandidate || "N/A"}</Text>
          </div>

          <Divider />

          {/* Expandable Sections */}
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={8}>
              <Row justify="space-between" align="middle">
                <Title level={5} style={{ margin: 0 }}>
                  Profile Summary
                </Title>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  iconPosition="end"
                  icon={
                    expanded.summary ? <UpOutlined /> : <DownOutlined />
                  }
                  onClick={() => toggleSection("summary")}
                >
                  {expanded.summary ? "Hide" : "Show"}
                </Button>
              </Row>
              {expanded.summary && (
                <Card size="small" bordered={false}>
                  <Text>
                    {publicData?.bio || publicData?.onBoardingInfo?.profileSummary || 'No profile summary provided'}
                  </Text>
                </Card>
              )}
            </Col>

            <Col xs={24} sm={8}>
              <Row justify="space-between" align="middle">
                <Title level={5} style={{ margin: 0 }}>
                  Top Skills
                </Title>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  iconPosition="end"
                  icon={
                    expanded.skills ? <UpOutlined /> : <DownOutlined />
                  }
                  onClick={() => toggleSection("skills")}
                >
                  {expanded.skills ? "Hide" : "Show"}
                </Button>
              </Row>
              {expanded.skills && (
                <Card size="small" bordered={false}>
                  {Array.isArray(publicData?.skills) && publicData?.skills?.length > 0 ? (
                    <ul style={{ margin: 0 }}>
                      {publicData.skills
                        .sort((a, b) => b.skillExp - a.skillExp) // sort descending by experience
                        .slice(0, 10) // show only top 10 skills
                        .map((skill, index) => (
                          <div key={index}>
                            <li>
                              {skill.skillExp} {skill.skillExp > 1 ? 'years' : 'year'} in{' '}
                              {skill.name?.charAt(0).toUpperCase() + skill.name?.slice(1)}
                            </li>
                          </div>
                        ))}
                    </ul>
                  ) : (
                    <Text>No skills listed</Text>
                  )}
                </Card>
              )}
            </Col>

            <Col xs={24} sm={8}>
              <Row justify="space-between" align="middle">
                <Title level={5} style={{ margin: 0 }}>
                  Certifications & Awards
                </Title>
                <Button
                  type="link"
                  size="small"
                  style={{ padding: 0 }}
                  iconPosition="end"
                  icon={
                    expanded.certifications ? <UpOutlined /> : <DownOutlined />
                  }
                  onClick={() => toggleSection("certifications")}
                >
                  {expanded.certifications ? "Hide" : "Show"}
                </Button>
              </Row>
              {expanded.certifications && (
                <Card size="small" bordered={false}>
                  {Array.isArray(publicData?.certificationsAndAwards) && publicData?.certificationsAndAwards?.length > 0 ? (
                    <ul style={{ margin: 0 }}>
                      {publicData.certificationsAndAwards.map((cert, index) => (
                        <li key={index}>
                          {cert.title?.charAt(0).toUpperCase() + cert.title?.slice(1)} <Space />
                          {cert?.issuedBy ? `Issued by ${cert?.issuedBy}` : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <Text>No certifications or awards listed</Text>
                  )}
                </Card>
              )}
            </Col>
          </Row>

          {/* Footer */}
          <Divider />
          <Row justify="end">
            <Space>
              <Button
                size="large"
                type="primary"
                onClick={() => {
                  history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id: id, slug: slug }, {}));
                }}
              >
                View Profile
              </Button>
              {canInvite && (
                <Button
                  size="large"
                  icon={<ExportOutlined />}
                  loading={inviteLoading}
                  onClick={handleInviteClick}
                >
                  Invite to Job
                </Button>
              )}
              {!footer && !isOwnListing && canAddToPreferred(currentUser) && (
                <AddToPreferredButton partyId={author?.id?.uuid} currentUser={currentUser} />
              )}
            </Space>
          </Row>
          {footerArea}

          {/* Invite to Job Modal */}
          <Modal
            title="Invite to a Job"
            open={isInviteModalOpen}
            onCancel={() => {
              setInviteModalOpen(false);
              setSelectedJobForInvitation(null);
            }}
            footer={[
              <Button key="cancel" onClick={() => setInviteModalOpen(false)}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                disabled={!selectedJobForInvitation}
                loading={inviteLoading}
                onClick={handleSendInvitation}
              >
                Invite
              </Button>,
            ]}
          >
            <div style={{ marginBottom: '24px' }}>
              {/* Already Invited Section */}
              {jobRfpListings.filter(l =>
                invitedTo.includes(l.id.uuid) ||
                l?.attributes?.publicData?.invitedUsersToPosting?.find(
                  invited => invited?.toUserId === listingAdminData?.id?.uuid
                )
              ).length > 0 && (
                  <div style={{
                    backgroundColor: '#f6f8fa',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    marginBottom: '20px',
                    border: '1px solid #e1e4e8',
                  }}>
                    <p style={{
                      margin: '0 0 8px 0',
                      fontSize: '13px',
                      fontWeight: '600',
                      color: '#57606a',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}>
                      Already Invited To
                    </p>
                    <p style={{
                      margin: 0,
                      fontSize: '14px',
                      color: '#24292f',
                      lineHeight: '1.5',
                    }}>
                      {jobRfpListings
                        .filter(l =>
                          invitedTo.includes(l.id.uuid) ||
                          l?.attributes?.publicData?.invitedUsersToPosting?.find(
                            invited => invited?.toUserId === listingAdminData?.id?.uuid
                          )
                        )
                        .map(l => l.attributes.title)
                        .join(', ')}
                    </p>
                  </div>
                )}

              {/* Job Selection */}
              <label style={{
                display: 'block',
                marginBottom: '8px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#24292f',
              }}>
                Select a job to invite this candidate to:
              </label>
              <Select
                style={{ width: '100%' }}
                value={selectedJobForInvitation}
                placeholder="Select a Job"
                onChange={value => setSelectedJobForInvitation(value)}
                options={
                  jobRfpListings
                    .filter(l =>
                      !invitedTo.includes(l.id.uuid) &&
                      !l?.attributes?.publicData?.invitedUsersToPosting?.find(
                        invited => invited?.toUserId === listingAdminData?.id?.uuid
                      ) &&
                      l?.attributes?.publicData?.listingType === 'job_listing'
                    )
                    .map(l => ({
                      value: l.id.uuid,
                      label: l.attributes.title,
                    }))
                }
              />
            </div>
          </Modal>
        </Card>
      </>
    );
  }

  if (listingIsAgency) {
    const publicData = currentListing?.attributes?.publicData;
    const websiteLink = publicData?.agencyProfile?.agencyWebsite || "";
    const agencyLinkedInPage = publicData?.agencyProfile?.agencyLinkedInPage || "";
    const profileImageVariants = author?.profileImage?.attributes?.variants || {};
    const listingImage = profileImageVariants['square-small2x']?.url || profileImageVariants['square-small']?.url;

    return (
      <Card
        style={{
          margin: "12px 0",
          minHeight: "380px"
        }}
      >
        {/* Header */}
        <Row justify="space-between" align="start">
          <Col>
            <Space align="start">
              {/* <Avatar
                size={64}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: "#f0f2f5",
                  border: "1px solid #e0e0e0",
                }}
              /> */}
              {listingImage ? (
                <Avatar
                  size={64}
                  src={listingImage}
                  style={{
                    backgroundColor: "#f0f2f5",
                    border: "1px solid #e0e0e0",
                  }}
                />
              ) : (
                <Avatar
                  size={64}
                  style={{
                    backgroundColor: "#f0f2f5",
                    border: "1px solid #e0e0e0",
                    color: 'grey'
                  }}
                >
                  {currentListing?.attributes?.title ? currentListing?.attributes?.title.charAt(0).toUpperCase() : '?'}
                </Avatar>
              )}
              <div>
                <Space size="small" align="center" className={css.spaceVerticalMobile}>
                  <Title level={4} style={{ marginBottom: 4 }}>
                    {currentListing?.attributes?.title}
                  </Title>
                  {isListingCompanyVerified && (
                    <Text type="success" strong style={{ margin: 0 }}><CheckCircleOutlined /> Company verified</Text>
                  )}
                  {agencyLinkedInPage !== '' && (
                    <a
                      href={ensureHttps(agencyLinkedInPage)}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginLeft: 8, fontSize: 14, fontWeight: "normal" }}
                    >
                      <span style={{ color: "#0A66C2" }}><LinkedinOutlined /> LinkedIn</span>
                    </a>
                  )}
                </Space><br />
                {websiteLink !== '' && (
                  <Space className={css.iconLists}>
                    <GlobalOutlined />
                    <a
                      href={ensureHttps(websiteLink)}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Website
                    </a>
                  </Space>
                )}
                <br />
                <Text type="secondary">
                  {publicData?.jobTitle || 'No job title provided'}
                </Text>
              </div>
            </Space>
          </Col>

          <Col>
            <Space wrap>
              {/* {Array.isArray(verified) && verified?.includes('identity') && (
                <Tag color="green">
                  <IdcardOutlined /> ID
                </Tag>
              )}
              {Array.isArray(verified) && verified?.includes('work') && (
                <Tag color="green">
                  <SolutionOutlined /> Work
                  </Tag>
              )}
              {Array.isArray(verified) && verified?.includes('education') &&
                <Tag color="green">
                  <ClockCircleOutlined /> Education
                </Tag>
              }
              {Array.isArray(verified) && verified?.includes('experience') &&
                <Tag color="green">
                  <BookOutlined /> Experience
                </Tag>
              } */}
              {/* <Tag color="green"><SafetyCertificateOutlined /> KYB Verified</Tag>
              <Tag color="green"><FileTextOutlined /> Tax ID Verified</Tag>
              <Tag color="green"><InsuranceOutlined /> Insurance Verified</Tag> */}
            </Space>
          </Col>
        </Row>

        <Divider />

        {/* Summary Info */}
        <Row gutter={[24, 12]} className={css.iconLists}>
          <Col xs={24} md={8}>
            <Text>
              <EnvironmentOutlined /> {publicData?.agencyProfile?.address?.selectedPlace?.address
                ? getCityStateCountry(publicData?.agencyProfile?.address?.selectedPlace?.address)
                : 'Location not specified'} · {Array.isArray(publicData?.onBoardingInfo?.deliveryModels) && publicData?.onBoardingInfo?.deliveryModels?.length > 0
                  ? publicData?.onBoardingInfo?.deliveryModels?.join(', ')
                  : 'Delivery models not specified'}
            </Text>
          </Col>
          <Col xs={24} md={8}>
            <Text><TeamOutlined /> {publicData?.onBoardingInfo?.employeeCount || 'Not specified'} Employees</Text>
            {/* How many consultants the supplier holds Linked, as the search counted them. */}
            {typeof currentListing?.attributes?.linkedConsultantCount === 'number' ? (
              <>
                <br />
                <Text data-testid="agency-linked-consultants">
                  {intl.formatMessage({ id: 'ListingCard.linkedConsultants' }, { count: currentListing.attributes.linkedConsultantCount })}
                </Text>
              </>
            ) : null}
          </Col>
          <Col xs={24} md={8}>
            <Text>
              <CalendarOutlined />{' '}
              {(() => {
                const dateStr = publicData?.onBoardingInfo?.dateOfFormation;
                if (!dateStr) return 'Founded: N/A';

                const date = new Date(dateStr);
                if (isNaN(date.getTime())) return 'Founded: Invalid date';

                const year = date.getFullYear();
                const currentYear = new Date().getFullYear();
                const diff = currentYear - year === 0 ? 'This' : currentYear - year;

                return `Founded: ${year} (${diff} yr${diff > 1 ? 's' : ''})`;
              })()}
            </Text>
          </Col>
        </Row>

        <Divider />

        {/* 2x2 Grid for the 4 sections */}
        <Row gutter={[24, 24]}>
          {/* Staffing Services */}
          <Col xs={24} md={12}>
            <Row justify="space-between" align="middle">
              <Title level={5} style={{ margin: 0 }}>
                Staffing Services
              </Title>
              <Button
                type="link"
                onClick={(e) => toggleSection("staffing", e)}
                style={{ padding: 0 }}
                iconPosition="end"
                icon={expanded.staffing ? <UpOutlined /> : <DownOutlined />}
              >
                {expanded.staffing ? "Hide" : "Show"}
              </Button>
            </Row>
            {expanded.staffing && (
              <Card size="small" bordered={false} style={{ boxShadow: "none" }}>
                <Space direction="vertical">
                  <Text>
                    <b>Service Domains:</b> <Space />
                    {Array.isArray(publicData?.staffingServiceDomains) && publicData?.staffingServiceDomains?.length > 0
                      ? publicData?.staffingServiceDomains?.join(', ')
                      : 'Not specified'}
                  </Text>
                  <Text>
                    <b>Technology Focus:</b> <Space />
                    {Array.isArray(publicData?.staffingTechFocSpec) && publicData?.staffingTechFocSpec?.length > 0
                      ? publicData?.staffingTechFocSpec?.join(', ')
                      : 'Not specified'}
                  </Text>
                  <Text>
                    <b>Roles Covered:</b> <Space />
                    {Array.isArray(publicData?.staffingRolesOffered) && publicData?.staffingRolesOffered?.length > 0
                      ? publicData?.staffingRolesOffered?.map((role, index) => {
                        if (index === publicData?.staffingRolesOffered?.length - 1) {
                          return role?.name?.charAt(0).toUpperCase() + role?.name?.slice(1);
                        }
                        return role?.name?.charAt(0).toUpperCase() + role?.name?.slice(1) + ', ';
                      })
                      : 'Not specified'}
                  </Text>
                  <Text>
                    <b>Placement Types:</b> <Space />
                    {Array.isArray(publicData?.onBoardingInfo?.contractTypesSupported) && publicData?.onBoardingInfo?.contractTypesSupported?.length > 0
                      ? publicData?.onBoardingInfo?.contractTypesSupported?.join(', ')
                      : 'Not specified'}
                  </Text>
                </Space>
              </Card>
            )}
          </Col>

          {/* Technology Services */}
          <Col xs={24} md={12}>
            <Row justify="space-between" align="middle">
              <Title level={5} style={{ margin: 0 }}>
                Technology Services
              </Title>
              <Button
                type="link"
                onClick={(e) => toggleSection("tech", e)}
                style={{ padding: 0 }}
                iconPosition="end"
                icon={expanded.tech ? <UpOutlined /> : <DownOutlined />}
              >
                {expanded.tech ? "Hide" : "Show"}
              </Button>
            </Row>
            {expanded.tech && (
              <Card size="small" bordered={false} style={{ boxShadow: "none" }}>
                <Space direction="vertical">
                  <Text>
                    <b>Service Domains:</b> <Space />
                    {Array.isArray(publicData?.implementationServiceDomains) && publicData?.implementationServiceDomains?.length > 0
                      ? publicData?.implementationServiceDomains?.join(', ')
                      : 'Not specified'}
                  </Text>
                  <Text>
                    <b>Technology Focus:</b> <Space />
                    {Array.isArray(publicData?.implementationTechFocSpec) && publicData?.implementationTechFocSpec?.length > 0
                      ? publicData?.implementationTechFocSpec?.join(', ')
                      : 'Not specified'}
                  </Text>
                  <Text>
                    <b>Engagement Models:</b> <Space />
                    {Array.isArray(publicData?.onBoardingInfo?.projectTypesDelivered) && publicData?.onBoardingInfo?.projectTypesDelivered?.length > 0
                      ? publicData?.onBoardingInfo?.projectTypesDelivered?.join(', ')
                      : 'Not specified'}
                  </Text>
                </Space>
              </Card>
            )}
          </Col>

          {/* Compliance & Certifications */}
          <Col xs={24} md={12}>
            <Row justify="space-between" align="middle">
              <Title level={5} style={{ margin: 0 }}>
                Compliance & Frameworks
              </Title>
              <Button
                type="link"
                onClick={(e) => toggleSection("compliance", e)}
                style={{ padding: 0 }}
                iconPosition="end"
                icon={expanded.compliance ? <UpOutlined /> : <DownOutlined />}
              >
                {expanded.compliance ? "Hide" : "Show"}
              </Button>
            </Row>
            {expanded.compliance && (
              <Card size="small" bordered={false} style={{ boxShadow: "none" }}>
                <Space direction="vertical">
                  <Text>
                    {Array.isArray(publicData?.onBoardingInfo?.certificationsFrameworks) && publicData?.onBoardingInfo?.certificationsFrameworks?.length > 0
                      ? publicData?.onBoardingInfo?.certificationsFrameworks?.join(', ')
                      : 'Not specified'}
                  </Text>
                </Space>
              </Card>
            )}
          </Col>

          {/* Engagement Snapshot */}
          <Col xs={24} md={12}>
            <Row justify="space-between" align="middle">
              <Title level={5} style={{ margin: 0 }}>
                Engagement Snapshot
              </Title>
              <Button
                type="link"
                onClick={(e) => toggleSection("engagement", e)}
                style={{ padding: 0 }}
                iconPosition="end"
                icon={expanded.engagement ? <UpOutlined /> : <DownOutlined />}
              >
                {expanded.engagement ? "Hide" : "Show"}
              </Button>
            </Row>
            {expanded.engagement && (
              <Card size="small" bordered={false} style={{ boxShadow: "none" }}>
                <Space direction="vertical">
                  <Text>
                    <b>Key Management Personnel:</b> <Space />
                    {Array.isArray(publicData?.keyManagementPersonnel) && publicData?.keyManagementPersonnel?.length > 0
                      ? publicData?.keyManagementPersonnel?.map((person, index) => {
                        if (index === publicData?.keyManagementPersonnel?.length - 1) {
                          return person?.name?.charAt(0).toUpperCase() + person?.name?.slice(1) + ' - ' + person?.role?.charAt(0).toUpperCase() + person?.role?.slice(1);
                        }
                        return person?.name?.charAt(0).toUpperCase() + person?.name?.slice(1) + ' - ' + person?.role?.charAt(0).toUpperCase() + person?.role?.slice(1) + ', ';
                      })
                      : 'Not specified'}
                  </Text>
                  {Array.isArray(publicData?.staffingGeographiesServed) && publicData?.staffingGeographiesServed?.length > 0 && (
                    <Text>
                      <b>Staffing Geographies Served: </b> <Space />
                      {publicData?.staffingGeographiesServed?.join(', ')}
                    </Text>
                  )}
                  {Array.isArray(publicData?.implementationGeographiesServed) && publicData?.implementationGeographiesServed?.length > 0 && (
                    <Text>
                      <b>Technology Geographies Served: </b> <Space />
                      {publicData?.implementationGeographiesServed?.join(', ')}
                    </Text>
                  )}
                </Space>
              </Card>
            )}
          </Col>
        </Row>

        {/* Footer */}
        <Divider style={{ marginTop: 24 }} />
        <Row justify="end">
          <Space>
            <Button
              size="large"
              type="primary"
              onClick={() => {
                history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id: id, slug: slug }, {}));
              }}
            >
              View Profile
            </Button>
            {canInvite && (
              <Button
                size="large"
                icon={<ExportOutlined />}
                loading={inviteLoading}
                onClick={handleInviteClick}
              >
                {agencyType === 'Implementation Service'
                  ? 'Invite to RFP'
                  : agencyType === 'Staffing Services'
                    ? 'Invite to Job'
                    : 'Invite to Job/RFP'
                }
              </Button>
            )}
            {!footer && !isOwnListing && canAddToPreferred(currentUser, 'agency') && (
              <AddToPreferredButton side="suppliers" agencyType={agencyType} partyId={author?.id?.uuid} currentUser={currentUser} />
            )}
          </Space>
        </Row>

        {/* Invite to Job/RFP Modal */}
        <Modal
          title={
            agencyType === 'Staffing and Implementation Services'
              ? 'Invite to a Job/RFP'
              : agencyType === 'Staffing Services'
                ? 'Invite to a Job'
                : agencyType === 'Implementation Service'
                  ? 'Invite to an RFP'
                  : 'Invite to a Job'
          }
          open={isInviteModalOpen}
          onCancel={() => {
            setInviteModalOpen(false);
            setSelectedJobForInvitation(null);
          }}
          footer={[
            <Button key="cancel" onClick={() => setInviteModalOpen(false)}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              disabled={!selectedJobForInvitation}
              loading={inviteLoading}
              onClick={handleSendInvitation}
            >
              Invite
            </Button>,
          ]}
        >
          <div style={{ marginBottom: '24px' }}>
            {/* Already Invited Section */}
            {jobRfpListings.filter(l =>
              invitedTo.includes(l.id.uuid) ||
              l?.attributes?.publicData?.invitedUsersToPosting?.find(
                invited => invited?.toUserId === listingAdminData?.id?.uuid
              )
            ).length > 0 && (
                <div style={{
                  backgroundColor: '#f6f8fa',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  marginBottom: '20px',
                  border: '1px solid #e1e4e8',
                }}>
                  <p style={{
                    margin: '0 0 8px 0',
                    fontSize: '13px',
                    fontWeight: '600',
                    color: '#57606a',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}>
                    Already Invited To
                  </p>
                  <p style={{
                    margin: 0,
                    fontSize: '14px',
                    color: '#24292f',
                    lineHeight: '1.5',
                  }}>
                    {jobRfpListings
                      .filter(l =>
                        invitedTo.includes(l.id.uuid) ||
                        l?.attributes?.publicData?.invitedUsersToPosting?.find(
                          invited => invited?.toUserId === listingAdminData?.id?.uuid
                        )
                      )
                      .map(l => l.attributes.title)
                      .join(', ')}
                  </p>
                </div>
              )}

            {/* Info message for dual-type agencies */}
            {agencyType === 'Staffing and Implementation Services' && (
              <div style={{
                backgroundColor: '#fffbeb',
                padding: '12px 16px',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #fde68a',
                display: 'flex',
                alignItems: 'start',
                gap: '8px',
              }}>
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>ℹ️</span>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#92400e',
                  lineHeight: '1.5',
                }}>
                  You can invite this supplier to either a job or an RFP, but only one at a time.
                </p>
              </div>
            )}

            {/* Jobs Selection Section - show for Staffing Provider or Staffing and Implementation Services */}
            {(agencyType === 'Staffing Services' || agencyType === 'Staffing and Implementation Services') && (
              <div style={{
                marginBottom: agencyType === 'Staffing and Implementation Services' ? '20px' : '0',
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#24292f',
                }}>
                  Select a job to invite this supplier to:
                </label>
                <Select
                  style={{ width: '100%' }}
                  value={
                    selectedJobForInvitation &&
                      jobRfpListings.find(l => l.id.uuid === selectedJobForInvitation)?.attributes?.publicData?.listingType === 'job_listing'
                      ? selectedJobForInvitation
                      : undefined
                  }
                  placeholder="Select a Job"
                  onChange={value => setSelectedJobForInvitation(value)}
                  options={
                    jobRfpListings
                      .filter(l =>
                        !invitedTo.includes(l.id.uuid) &&
                        !l?.attributes?.publicData?.invitedUsersToPosting?.find(
                          invited => invited?.toUserId === listingAdminData?.id?.uuid
                        ) &&
                        l?.attributes?.publicData?.listingType === 'job_listing'
                      )
                      .map(l => ({
                        value: l.id.uuid,
                        label: l.attributes.title,
                      }))
                  }
                />
              </div>
            )}

            {/* Message when agency cannot be invited to Jobs */}
            {agencyType === 'Implementation Service' && (
              <div style={{
                backgroundColor: '#f0f4f8',
                padding: '12px 16px',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #d0d7de',
                display: 'flex',
                alignItems: 'start',
                gap: '8px',
              }}>
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>ℹ️</span>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#57606a',
                  lineHeight: '1.5',
                }}>
                  This supplier is an Implementation Service provider and cannot be invited to Jobs. They can only be invited to RFPs.
                </p>
              </div>
            )}

            {/* RFPs Selection Section */}
            {(agencyType === 'Implementation Service' || agencyType === 'Staffing and Implementation Services') && (
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#24292f',
                }}>
                  Select an RFP to invite this supplier to:
                </label>
                <Select
                  style={{ width: '100%' }}
                  value={
                    selectedJobForInvitation &&
                      jobRfpListings.find(l => l.id.uuid === selectedJobForInvitation)?.attributes?.publicData?.listingType === 'rfp_listing'
                      ? selectedJobForInvitation
                      : undefined
                  }
                  placeholder="Select an RFP"
                  onChange={value => setSelectedJobForInvitation(value)}
                  options={
                    jobRfpListings
                      .filter(l =>
                        !invitedTo.includes(l.id.uuid) &&
                        !l?.attributes?.publicData?.invitedUsersToPosting?.find(
                          invited => invited?.toUserId === listingAdminData?.id?.uuid
                        ) &&
                        l?.attributes?.publicData?.listingType === 'rfp_listing'
                      )
                      .map(l => ({
                        value: l.id.uuid,
                        label: l.attributes.title,
                      }))
                  }
                />
              </div>
            )}

            {/* Message when agency cannot be invited to RFPs */}
            {agencyType === 'Staffing Services' && (
              <div style={{
                backgroundColor: '#f0f4f8',
                padding: '12px 16px',
                borderRadius: '6px',
                marginTop: '20px',
                border: '1px solid #d0d7de',
                display: 'flex',
                alignItems: 'start',
                gap: '8px',
              }}>
                <span style={{ fontSize: '16px', lineHeight: '1.5' }}>ℹ️</span>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: '#57606a',
                  lineHeight: '1.5',
                }}>
                  This supplier is a Staffing Provider and cannot be invited to RFPs. They can only be invited to Jobs.
                </p>
              </div>
            )}
          </div>
        </Modal>
        {footerArea}
      </Card>
    );
  }

  // A hiring company's card shows what the company entered on its own profile.
  if (listingIsCustomer) {
    const customerProfile = publicData?.customerProfile || {};
    const companyName = title || customerProfile.name || '';
    const location = getCityStateCountry(customerProfile.address?.selectedPlace?.address);
    const profileImageVariants = author?.profileImage?.attributes?.variants || {};
    const logoUrl = profileImageVariants['square-small2x']?.url || profileImageVariants['square-small']?.url;

    return (
      <Card style={{ margin: "12px 0" }}>
        <Row justify="space-between" align="start">
          <Col>
            <Space align="start">
              <Avatar
                size={64}
                src={logoUrl || undefined}
                style={{ backgroundColor: "#f0f2f5", border: "1px solid #e0e0e0", color: 'grey' }}
              >
                {companyName ? companyName.charAt(0).toUpperCase() : '?'}
              </Avatar>
              <div>
                <Space size="small" align="center" className={css.spaceVerticalMobile}>
                  <Title level={4} style={{ marginBottom: 4 }}>
                    <NamedLink name="ListingPage" params={{ id, slug }}>
                      {companyName}
                    </NamedLink>
                  </Title>
                  {isListingCompanyVerified && (
                    <Text type="success" strong style={{ margin: 0 }}><CheckCircleOutlined /> Company verified</Text>
                  )}
                </Space>
                {location ? (
                  <>
                    <br />
                    <Text type="secondary" className={css.iconLists}>
                      <EnvironmentOutlined /> {location}
                    </Text>
                  </>
                ) : null}
              </div>
            </Space>
          </Col>
        </Row>

        <Divider style={{ marginTop: 24 }} />
        <Row justify="end">
          <Space>
            <Button
              size="large"
              type="primary"
              onClick={() => {
                history.push(createResourceLocatorString('ListingPage', routeConfiguration, { id, slug }, {}));
              }}
            >
              View Profile
            </Button>
          </Space>
        </Row>
        {footerArea}
      </Card>
    );
  }

  // A listing of any other type has no card.
  return null;
};

export default ListingCard;
